import { useField } from 'formik'
import Styles from './GeneratedArchetypePreview.module.scss'
import { useEffect, useMemo } from 'react'
import { useGenerateDesignContentSync } from 'hooks/queries/workspaces/useGenerateDesignContentSync'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { observer } from 'mobx-react-lite'
import { useChannelsListContext } from '../ChannelsListContext/ChannelsListContext'
import { GeneratedArchetypePreviewProps } from './Types'

const GeneratedArchetypePreview = observer(({ channel }: GeneratedArchetypePreviewProps) => {
  const { archetype, width, height } = channel
  const { updateChannels } = useChannelsListContext()
  const [{ value: topic }] = useField('topic')
  const [{ value: language }] = useField('language')
  const [{ value: brand_voice_id }] = useField('brand_voice_id')
  const [{ value: tone }] = useField('tone')
  const [{ value: brand_kit_id }] = useField('brand_kit_id')
  const [{ value: brand_visual_kit_id }] = useField('brand_visual_kit_id')
  const generateMutation = useGenerateDesignContentSync({ retry: 3 })
  const toneDep = JSON.stringify(tone)

  useEffect(() => {
    const controller = new AbortController()

    generateMutation.mutate({
      topic,
      language,
      brand_voice_id,
      tone,
      brand_kit_id,
      brand_visual_kit_id,
      apply_brand: true,
      signal: controller.signal,
      archetype,
      width,
      height,
    })

    return () => {
      controller.abort()
    }
  }, [archetype, topic, language, brand_voice_id, toneDep, brand_kit_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const request = generateMutation.data
  const generatedContent = request?.generated_content

  const content = useMemo(() => {
    if (generatedContent) {
      return JSON.parse(generatedContent)
    }
  }, [generatedContent])

  useEffect(() => {
    if (content) {
      updateChannels([{ ...channel, content }])
    }
  }, [content]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={Styles.designPreviewContainer} style={{ aspectRatio: width && height ? width / height : 1 }}>
      <LoadingShimmerer className={Styles.designPreview} show={generateMutation.isLoading}>
        {content && (
          <div className={Styles.designPreview} data-testid="archetype-preview">
            <DesignContentPreview content={content} />
          </div>
        )}
      </LoadingShimmerer>
    </div>
  )
})

export { GeneratedArchetypePreview }
